import { APP_VERSION } from '@/version';
import { CommonModule, Location, LocationStrategy, NgFor, PathLocationStrategy } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  inject,
  OnDestroy,
  OnInit
} from '@angular/core';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { IonicModule } from '@ionic/angular';
import { filter, Subject, takeUntil } from 'rxjs';
import { ProfiloRuolo, RuoloAttivo } from 'src/app/core/models/user.model';
import { AppStore } from 'src/app/core/services/store/app.store';
import { UserRepository } from 'src/app/core/services/user.repository.service';
import { UserService } from 'src/app/core/services/user.service';
import { LogoutComponent } from './shared/components/logout/logout.component';
import { Console } from './shared/providers/custom-logger';

interface NavItem {
  readonly name: 'Homepage' | 'Promotore' | 'Convenzionato' | 'Premi';
  readonly link: string;
  readonly icon: string;
  readonly level: number;
}

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
  standalone: true,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],

  providers: [
    Location,
    { provide: LocationStrategy, useClass: PathLocationStrategy },
    { provide: 'router', useValue: { enableTracing: false } }
  ],
  imports: [IonicModule, NgFor, RouterLink, LogoutComponent, CommonModule],
})
export class AppComponent implements OnInit, OnDestroy {
  private log_label = 'AppComponent';
  private destroy$ = new Subject<void>();

  versione: string = APP_VERSION;
  parametri_header: Record<'manifestazione' | 'convenzionato' | 'promotore' | 'default', string> = {
    manifestazione: 'Ti Premia!',
    convenzionato: 'Convenzionato',
    promotore: 'Promotore',
    default: 'APP',
  };
  NAV: readonly NavItem[] = [
    {
      name: 'Homepage',
      link: '/app',
      icon: 'home',
      level: 0,
    },
    {
      name: 'Promotore',
      link: '/app/promotore-page',
      icon: 'megaphone',
      level: 0,
    },
    {
      name: 'Convenzionato',
      link: '/app/convenzionato-page',
      icon: 'qr-code',
      level: 0,
    },
    {
      name: 'Premi',
      link: '/app/manifestazione-page',
      icon: 'gift',
      level: 0,
    },
    // {
    //   name: 'Bot',
    //   link: '/app/bot',
    //   icon: 'chatbubble-ellipses',
    //   level: 0,
    // },
  ];
  current_url: string = '';
  router: Router = inject(Router);
  private cdr = inject(ChangeDetectorRef);

  // Aggiungere i flag per la gestione del menu
  isPromotore = false;
  isConvenzionato = false;
  isManifestazione = false;

  // Aggiungere i servizi come proprietà della classe
  private appStore = inject(AppStore);
  private userService = inject(UserService);
  private userRepository = inject(UserRepository);

  async ngOnInit() {
    const log_pref = `${this.log_label}.ngOnInit() `;
    
    // Cache busting per PWA - elimina tutte le cache dell'app
    if ('caches' in window) {
      try {
        const cacheNames = await caches.keys();
        for (const cacheName of cacheNames) {
          if (cacheName.includes('primo') || cacheName.includes('app') || cacheName.includes('ngsw')) {
            await caches.delete(cacheName);
            Console.log(log_pref + 'Cache eliminata:', cacheName, 'std');
          }
        }
        Console.log(log_pref + 'Cache busting completato', 'succ');
      } catch (error) {
        Console.log(log_pref + 'Errore durante cache busting:', error, 'err');
      }
    }
    
    // Inizializza current_url immediatamente
    this.current_url = this.router.url;
    this.applyBodyClassesFromUrl(this.current_url);
    Console.log(log_pref + 'URL iniziale:', this.router.url, 'std');

    // Aggiorna current_url e le classi del body ad ogni navigazione
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe((event) => {
        this.current_url = event.urlAfterRedirects || event.url;
        this.applyBodyClassesFromUrl(this.current_url);
        this.cdr.detectChanges();
        Console.log(log_pref + 'URL aggiornato:', this.current_url, 'std');
      });
    
    // Aggiorna lo stato del menu
    this.updateMenuState();

    // Sincronizza il menu con variazioni di utente e ruolo attivo
    this.userService.user$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.updateMenuState();
    });

    this.userService.activeRole$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.updateMenuState();
    });
  }

  /** Applica le classi CSS sul body in base all'URL (promotore/convenzionato/manifestazione). */
  private applyBodyClassesFromUrl(url: string): void {
    if (url.includes('/promotore')) {
      document.body.classList.add('promotore');
      document.body.classList.remove('convenzionato');
      document.body.classList.remove('manifestazione');
    } else if (url.includes('/convenzionato')) {
      document.body.classList.add('convenzionato');
      document.body.classList.remove('promotore');
      document.body.classList.remove('manifestazione');
    } else if (url.includes('/manifestazione')) {
      document.body.classList.add('manifestazione');
      document.body.classList.remove('promotore');
      document.body.classList.remove('convenzionato');
    } else {
      document.body.classList.remove('convenzionato');
      document.body.classList.remove('promotore');
      document.body.classList.remove('manifestazione');
    }
  }

  // Aggiungere metodo per aggiornare lo stato del menu
  public updateMenuState(): void {
    const currentUser = this.userRepository.getUser();
    
    if (this.appStore.areContactsVerified()) {
      const targetRole = currentUser?.target as RuoloAttivo;
      const hasProfile = this.userService.isIscritto(targetRole as ProfiloRuolo);

      // Se target è null, attiva tutti i pulsanti
      if (targetRole === null || targetRole === '') {
        this.isManifestazione = true;
        this.isPromotore = true;
        this.isConvenzionato = true;
      } else {
        if (hasProfile) {
          // Se il profilo esiste, attiva tutti i pulsanti
          this.isManifestazione = true;
          this.isPromotore = true;
          this.isConvenzionato = true;
        } else {
          // Se il profilo non esiste, attiva solo il pulsante corrispondente al target
          this.isManifestazione = targetRole === 'manifestazione';
          this.isPromotore = targetRole === 'promotore';
          this.isConvenzionato = targetRole === 'convenzionato';
        }
      }
    } else {
      // Se i contatti non sono verificati, disabilita tutti i pulsanti
      this.isManifestazione = false;
      this.isPromotore = false;
      this.isConvenzionato = false;
    }

    // Forza il rendering in caso di update fuori ciclo
    this.cdr.detectChanges();
  }

  // Metodo per verificare se una voce del menu è disabilitata
  isMenuItemDisabled(nav: NavItem): boolean {
    switch (nav.name) {
      case 'Promotore':
        return !this.isPromotore;
      case 'Convenzionato':
        return !this.isConvenzionato;
      case 'Premi':
        return !this.isManifestazione;
      default:
        return false; // Homepage e altre voci sempre abilitate
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
